import store from '@/store';
import axios from 'axios';
import {Module, VuexModule, Mutation, Action} from 'vuex-module-decorators';
import { Mutations, Endpoints } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import DashboardNotification from '@/ts/instances/notifications/DashboardNotification';
import UpcomingEvent from '@/ts/instances/notifications/UpcomingEvent';
import LogNotification from '@/ts/instances/notifications/LogNotification';
import EventLog from '@/ts/instances/notifications/EventLog';
import NotificationType from '@/ts/instances/notifications/NotificationType';
import { TenantNotificationResponse, UpcomingEventResponse } from '@/ts/interfaces/NotificationInterfaces';
import { Connection } from '@/configuration/Connection';

@Module({name: 'TenantNotifications', namespaced: true})
export default class TenantNotifications extends VuexModule {

    /** Logs notifications */
    public tenantNotificationLogs: LogNotification[] = [];
    public tenantLogsPage: number = 1;
    public tenantLogsLastPage: number = 0;
    public tenantUnreadNotificationsCount: number = 0;

    /** Dashboards notifications */
    public dashboardNotifications: DashboardNotification[] = [];
    public dashboardPage: number = 1;
    public dashboardLastPage: number = 0;
    public dashboardActiveTab: string = 'all';

    /** Detailed notifications */
    public activeNotification: DashboardNotification = null;

    /** Upcoming Events */
    public upcomingEvents: UpcomingEvent[] = [];

    @Mutation
    public RESET_DASHBOARD_STORE_STATE() {
        this.dashboardNotifications  = [];
        this.dashboardPage           = 1;
        this.dashboardLastPage       = 0;
        this.dashboardActiveTab      = 'all';

        this.tenantNotificationLogs = [];
        this.tenantLogsPage         = 1;
        this.tenantLogsLastPage     = 0;
        this.tenantUnreadNotificationsCount = 0;

        this.activeNotification = null;
        this.upcomingEvents     = [];
    }

    get getDashboardNotifications(): DashboardNotification[] {
        return this.dashboardNotifications;
    }

    get getTenantNotificationLogs(): LogNotification[] {
        return this.tenantNotificationLogs;
    }

    get getTenantUnreadNotificationsCount(): number {
        return this.tenantUnreadNotificationsCount;
    }

    get getDashboardActiveTab(): string {
        return this.dashboardActiveTab;
    }

    get getDashboardPage(): number {
        return this.dashboardPage;
    }

    get getDashboardLastPage(): number {
        return this.dashboardLastPage;
    }

    get getTenantLogsPage(): number {
        return this.tenantLogsPage;
    }

    get getTenantLogsLastPage(): number {
        return this.tenantLogsLastPage;
    }

    get getActiveNotification(): DashboardNotification {
        return this.activeNotification;
    }

    get getUpcomingEvents(): UpcomingEvent[] {
        return this.upcomingEvents;
    }

    get getUpcomingEvent(): any {
        return (upcomingEventId: number) => this.upcomingEvents.find((ue) => ue.notificationId = upcomingEventId);
    }

    @Action
    public INITIALIZE_TENANT_LOG_SOCKET(vue: Vue) {
        if (!Connection.socketEnabled) {
            return;
        }

        if (store.state.Socket.tenantLogSocketInitialized) {
            return;
        }

        const userId  = store.getters['User/getUserId'];
        const channel = 'tenant-user.' + userId + '.log';

        vue.$echo.private(channel)
            .listen('.log.new', (payload: any) => {
                store.commit(Mutations.ADD_NOTIFICATIONS_LOG, payload);
            });

        store.commit(Mutations.SET_TENANT_LOG_SOCKET_INITIALIZED);
    }

    @Action
    public FETCH_TENANTS_NOTIFICATIONS_UNREAD_COUNT() {
        axios.get(Endpoints.Notifications.NOTIFICATIONS_UNREAD_COUNT)
            .then((response) => {
                store.commit(Mutations.SET_TENANT_UNREAD_NOTIFICATIONS_COUNT, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
        return;
    }

    @Action
    public FETCH_DASHBOARD_NOTIFICATIONS(requestObj: any) {
        return axios.get(Endpoints.Notifications.DASHBOARD_NOTIFICATIONS(), {
            params: {
                page: requestObj.page,
                type: requestObj.type,
            },
        })
            .then((response) => {
                if (requestObj.hardReload) {
                    store.commit(Mutations.RESET_DASHBOARD_NOTIFICATIONS);
                }

                if (response.data.data.length) {
                    store.commit(Mutations.SET_DASHBOARD_NOTIFICATIONS, response.data.data);
                    store.commit(Mutations.SET_DASHBOARD_PAGE, requestObj.page + 1);
                } else {
                    store.commit(Mutations.SET_LAST_DASHBOARD_PAGE);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_TENANT_LOGS_NOTIFICATIONS(requestObj: any) {
        return axios.get(Endpoints.Notifications.TENANT_NOTIFICATIONS_LOGS(), {
            params: {
                page: requestObj.page,
            },
        })
            .then((response) => {
                if (requestObj.hardReload) {
                    store.commit(Mutations.RESET_NOTIFICATIONS_LOGS);
                }

                if (response.data.data.length) {
                    store.commit(Mutations.SET_NOTIFICATIONS_LOGS, response.data.data);
                    store.commit(Mutations.SET_TENANT_LOGS_PAGE, requestObj.page + 1);
                } else {
                    store.commit(Mutations.SET_LAST_TENANT_LOGS_PAGE);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_TENANT_NOTIFICATION(notificationId: number) {
        return axios.get(Endpoints.Notifications.SHOW_TENANT_NOTIFICATION(notificationId))
            .then((response) => {
                store.commit(Mutations.SET_TENANT_ACTIVE_NOTIFICATION, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public MARK_TENANT_NOTIFICATION_AS_SEEN(notificationId: number) {
        axios.put(Endpoints.Notifications.MARK_ACTIVE_NOTIFICATION_AS_SEEN(notificationId))
            .then(() => {
                store.commit(Mutations.CHANGE_NOTIFICATION_SEEN_STATUS, notificationId);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public MARK_TENANT_ALL_NOTIFICATION_AS_SEEN() {
        axios.post(Endpoints.Notifications.TENANT_NOTIFICATIONS_READ_ALL)
            .then(() => {
                store.commit(Mutations.CHANGE_NOTIFICATIONS_SEEN_STATUS);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_TENANT_UPCOMING_EVENTS() {
        return axios.get(Endpoints.Notifications.TENANT_UPCOMING_EVENTS)
            .then((response) => {
                store.commit(Mutations.SET_TENANT_UPCOMING_EVENTS, response.data.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public SET_DASHBOARD_PAGE(page: number) {
        this.dashboardPage = page;
    }

    @Mutation
    public SET_TENANT_LOGS_PAGE(page: number) {
        this.tenantLogsPage = page;
    }

    @Mutation
    public SET_LAST_DASHBOARD_PAGE() {
        this.dashboardLastPage = this.dashboardPage;
    }

    @Mutation
    public SET_LAST_TENANT_LOGS_PAGE() {
        this.tenantLogsLastPage = this.tenantLogsPage;
    }

    @Mutation
    public SET_DASHBOARD_PAGE_ACTIVE_FILTER(tab: string) {
        this.dashboardActiveTab = tab;
    }

    @Mutation
    public RESET_DASHBOARD_NOTIFICATIONS() {
        this.dashboardLastPage = 0;
        this.dashboardNotifications = [];
    }

    @Mutation
    public RESET_NOTIFICATIONS_LOGS() {
        this.tenantLogsLastPage = 0;
        this.tenantNotificationLogs = [];
    }

    @Mutation
    public SET_DASHBOARD_NOTIFICATIONS(notificationData: TenantNotificationResponse[]) {
        for (const notification of notificationData) {
            const dashboardNotification = new DashboardNotification(
                notification.id,
                notification.eventId,
                notification.title,
                notification.text,
                notification.requiresResponse,
                notification.seen,
                notification.hasMedia,
                notification.publishedAt,
                notification.createdAt,
                notification.updatedAt,
                notification.startTime,
                notification.endTime,
                notification.likesCount,
                notification.commentsCount,
                notification.likesRecipients,
                notification.attenders,
                notification.place,
                notification.attending,
            );

            dashboardNotification.setNotificationAuthor(notification.author);
            dashboardNotification.setNotificationType(new NotificationType(
                notification.type.id,
                notification.type.key,
                notification.type.description,
            ));

            if (!this.dashboardNotifications.find((n) => n.id === dashboardNotification.id)) {
                this.dashboardNotifications.push(dashboardNotification);
            }
        }
    }

    @Mutation
    public SET_NOTIFICATIONS_LOGS(logs: any) {
        for (const log of logs) {
            const eventLog = new EventLog(
                log.eventLog.id,
                log.eventLog.message,
                log.eventLog.authorId,
                log.eventLog.featureId,
                log.eventLog.corporationId,
                log.eventLog.createdAt,
            );

            if (log.eventLog.ticket) {
                eventLog.setTicket(log.eventLog.ticket);
            }

            if (log.eventLog.booking) {
                eventLog.setBooking(log.eventLog.booking);
            }

            const notificationLog = new LogNotification(
                log.id,
                log.title,
                log.text,
                log.seen,
                log.createdAt,
                eventLog,
            );
            notificationLog.setAuthor(log.author);

            if (!this.tenantNotificationLogs.find((l) => l.id === notificationLog.id)) {
                this.tenantNotificationLogs.push(notificationLog);
            }
        }
    }

    @Mutation
    public ADD_NOTIFICATIONS_LOG(log: any) {
        const eventLog = new EventLog(
            log.eventLog.id,
            log.eventLog.message,
            log.eventLog.authorId,
            log.eventLog.featureId,
            log.eventLog.corporationId,
            log.eventLog.createdAt,
        );

        if (log.eventLog.ticket) {
            eventLog.setTicket(log.eventLog.ticket);
        }

        if (log.eventLog.booking) {
            eventLog.setBooking(log.eventLog.booking);
        }

        const notificationLog = new LogNotification(
            log.id,
            log.title,
            log.text,
            log.seen,
            log.createdAt,
            eventLog,
        );
        notificationLog.setAuthor(log.author);

        if (!this.tenantNotificationLogs.find((l) => l.id === notificationLog.id)) {
            this.tenantNotificationLogs.unshift(notificationLog);
            store.commit(Mutations.SET_TENANT_UNREAD_NOTIFICATIONS_COUNT, '+');
        }
    }

    @Mutation
    public SET_TENANT_ACTIVE_NOTIFICATION(notification: TenantNotificationResponse): void {
        if (notification === null) {
            this.activeNotification = null;
            return;
        }

        this.activeNotification = new DashboardNotification(
            notification.id,
            notification.eventId,
            notification.title,
            notification.text,
            notification.requiresResponse,
            notification.seen,
            notification.hasMedia,
            notification.publishedAt,
            notification.createdAt,
            notification.updatedAt,
            notification.startTime,
            notification.endTime,
            notification.likesCount,
            notification.commentsCount,
            notification.likesRecipients,
            notification.attenders,
            notification.place,
            notification.attending,
        );

        this.activeNotification.setNotificationAuthor(notification.author);
        this.activeNotification.setNotificationType(new NotificationType(
            notification.type.id,
            notification.type.key,
            notification.type.description,
        ));
    }

    @Mutation
    public SET_TENANT_UPCOMING_EVENTS(upcomingEvents: UpcomingEventResponse[]) {
        for (const event of upcomingEvents) {
            const upcomingEvent = new UpcomingEvent(
                event.id,
                event.type,
                event.startTime,
                event.title,
                event.thumbnail,
                event.attending,
                event.notificationId,
            );

            if (!this.upcomingEvents.find((u) => u.id === upcomingEvent.id)) {
                this.upcomingEvents.push(upcomingEvent);
            }
        }
    }

    @Mutation
    public SET_TENANT_UNREAD_NOTIFICATIONS_COUNT(operation: string|number) {
        if (typeof operation === 'string') {
            if (operation === '+') {
                this.tenantUnreadNotificationsCount++;
            }

            if (operation === '-') {
                this.tenantUnreadNotificationsCount--;
            }
        } else {
            this.tenantUnreadNotificationsCount = operation;
        }
    }

    @Mutation
    public CHANGE_NOTIFICATION_SEEN_STATUS(notificationId: number) {
        const logsNotification      = this.tenantNotificationLogs.find((n) => n.id === notificationId);
        const dashboardNotification = this.dashboardNotifications.find((n) => n.id === notificationId);

        if (logsNotification) {
            logsNotification.read();
        }

        if (dashboardNotification) {
            dashboardNotification.read();
        }

        store.commit(Mutations.SET_TENANT_UNREAD_NOTIFICATIONS_COUNT, '-');
    }

    @Mutation
    public CHANGE_NOTIFICATIONS_SEEN_STATUS() {
        this.tenantNotificationLogs.forEach((n) => n.read());
        store.commit(Mutations.SET_TENANT_UNREAD_NOTIFICATIONS_COUNT, 0);
    }

    @Mutation
    public ADD_NOTIFICATION_LIKE(data: any): void {
        const dashboardNotification = this.dashboardNotifications.find((n) => n.id === data.notificationId);

        if (dashboardNotification) {
            if (!dashboardNotification.likesRecipients.find((recipient: any) => recipient === store.getters['User/getUserId'])) {
                dashboardNotification.addLike(data.likesCount, store.getters['User/getUserId']);
            }
        }

        if (this.activeNotification && this.activeNotification.id === data.notificationId) {
            if (!this.activeNotification.likesRecipients.find((recipient: any) => recipient === store.getters['User/getUserId'])) {
                this.activeNotification.addLike(data.likesCount, store.getters['User/getUserId']);
            }

        }
    }

    @Mutation
    public REMOVE_NOTIFICATION_LIKE(data: any): void {
        const dashboardNotification = this.dashboardNotifications.find((n) => n.id === data.notificationId);

        if (dashboardNotification) {
            dashboardNotification.removeLike(data.likesCount, store.getters['User/getUserId']);
        }

        if (this.activeNotification && this.activeNotification.id === data.notificationId) {
            this.activeNotification.removeLike(data.likesCount, store.getters['User/getUserId']);
        }
    }

    @Mutation
    public CHANGE_UPCOMING_EVENT_ATTENDING_STATUS(data: any) {
        const upcomingEvent = this.upcomingEvents.find((ue) => ue.notificationId === data.notificationId);

        if (upcomingEvent) {
            upcomingEvent.changeAttending(data.status);
        }
    }

    @Mutation
    public INCREASE_COMMENT_COUNT(notificationId: number): void {
        const dashboardNotification = this.dashboardNotifications.find((n) => n.id === notificationId);
        dashboardNotification.increaseCommentCount();
    }

    @Mutation
    public DECREASE_COMMENT_COUNT(notificationId: number): void {
        const dashboardNotification = this.dashboardNotifications.find((n) => n.id === notificationId);
        dashboardNotification.decreaseCommentCount();
    }
}
